import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { format, isPast, isToday } from 'date-fns'
import { Search, Clock, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { projectService } from '@/services/projectService'
import { taskService } from '@/services/taskService'
import { DataTable } from '@/components/shared/DataTable'
import PriorityBadge from '@/components/ProjectCentral/PriorityBadge'
import TaskFilters from '@/components/ProjectCentral/TaskFilters'
import InlineStatusSelect from '@/components/ProjectCentral/InlineStatusSelect'
import TaskTimerButton from '@/components/ProjectCentral/TaskTimerButton'
import TaskDisplayId from '@/components/ProjectCentral/TaskDisplayId'
import { LabelChip } from '@/components/ProjectCentral/LabelsInput'
import ProjectLayout from '@/components/ProjectCentral/ProjectLayout'
import BulkActionBar from '@/components/ProjectCentral/BulkActionBar'
import api from '@/services/api'

const formatDuration = (seconds) => {
  if (!seconds) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const ProjectList = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})
  const [rowSelection, setRowSelection] = useState({}) // { [taskId]: true } for bulk edit

  // Build query params for API
  const queryParams = {
    ...filters,
    ...(search ? { search } : {}),
  }

  const { data: tasksData, isLoading, isError, error, refetch } = useQuery(
    ['projects', id, 'tasks', queryParams],
    () => taskService.getTasks(id, queryParams),
    { keepPreviousData: true, retry: (count, err) => err?.response?.status !== 403 && count < 3 }
  )

  const { data: statusData } = useQuery(
    ['projects', id, 'statuses'],
    () => projectService.getStatuses(id)
  )

  const { data: usersData } = useQuery(
    'team-members',
    async () => { const r = await api.get('/users/team-members'); return r.data }
  )

  const { data: projectData } = useQuery(
    ['projects', id],
    () => projectService.getProject(id)
  )

  const project = projectData?.data?.project
  const tasks = tasksData?.data?.tasks || []
  const statuses = statusData?.data?.statuses || []
  const users = usersData?.data?.users || []

  const handleTaskClick = (task) => navigate(`/projects/${id}/tasks/${task.id}`)

  // Bulk edit: only selected tasks that are still listed (after filtering) count
  const selectedIds = tasks.filter(t => rowSelection[String(t.id)]).map(t => t.id)

  const columns = [
    {
      id: 'display_id',
      header: 'ID',
      accessorFn: (row) => row.task_number || 0,
      cell: ({ row }) => {
        const t = row.original
        return (
          <TaskDisplayId projectId={id} projectKey={project?.key} taskId={t.id} taskNumber={t.task_number} taskType={t.type} />
        )
      },
      size: 80,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => {
        const t = row.original
        return (
          <div className="max-w-[340px]">
            <button
              className="text-left font-medium text-sm hover:text-primary transition-colors truncate block max-w-full"
              onClick={() => handleTaskClick(t)}
            >
              {t.title}
            </button>
            {(t.parent || t.labels?.length > 0 || t.childCount > 0) && (
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {t.parent && (
                  <span className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={t.parent.title}>
                    ↳ {project?.key}-{t.parent.task_number} {t.parent.title}
                  </span>
                )}
                {(t.labels || []).slice(0, 3).map(label => <LabelChip key={label} label={label} className="text-[10px]" />)}
                {t.childCount > 0 && <span className="text-[10px] text-muted-foreground">{t.childDoneCount}/{t.childCount} done</span>}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status?.name || '',
      cell: ({ row }) => (
        <InlineStatusSelect
          task={row.original}
          statuses={statuses}
          projectId={id}
        />
      ),
    },
    {
      id: 'assignee',
      header: 'Assignee',
      accessorFn: (row) => {
        const a = row.assignee
        return a?.employee ? `${a.employee.first_name} ${a.employee.last_name}` : a?.email || ''
      },
      cell: ({ row }) => {
        const assignee = row.original.assignee
        if (!assignee) return <span className="text-muted-foreground text-sm">—</span>
        const name = assignee.employee
          ? `${assignee.employee.first_name} ${assignee.employee.last_name}`
          : assignee.email
        return (
          <div className="flex items-center gap-1.5">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                {name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm truncate max-w-[120px]">{name}</span>
          </div>
        )
      },
    },
    {
      id: 'priority',
      header: 'Priority',
      accessorFn: (row) => ({ low: 1, medium: 2, high: 3, critical: 4 }[row.priority] || 0),
      cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      cell: ({ row }) => {
        const due = row.original.due_date
        if (!due) return <span className="text-muted-foreground text-sm">—</span>
        const date = new Date(due + 'T00:00:00')
        const overdue = isPast(date) && !isToday(date) && row.original.status?.category !== 'done'
        const today = isToday(date)
        return (
          <span className={cn(
            'text-sm',
            overdue && 'text-destructive font-medium',
            today && 'text-orange-600 font-medium'
          )}>
            {format(date, 'MMM d, yyyy')}
          </span>
        )
      },
    },
    {
      id: 'time_logged',
      header: 'Time Logged',
      enableSorting: false,
      cell: ({ row }) => {
        const task = row.original
        const total = task.timeEntries
          ?.filter(t => t.end_time)
          .reduce((sum, t) => sum + (t.duration || 0), 0) || 0
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {task.status?.category !== 'done' && (
              <TaskTimerButton
                taskId={task.id}
                projectId={task.project_id}
                taskTitle={task.title}
              />
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatDuration(total)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => {
        const d = row.original.created_at
        if (!d) return <span className="text-muted-foreground text-sm">—</span>
        return <span className="text-sm text-muted-foreground whitespace-nowrap">{format(new Date(d), 'MMM d, yyyy h:mm a')}</span>
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => {
        const d = row.original.updated_at
        if (!d) return <span className="text-muted-foreground text-sm">—</span>
        return <span className="text-sm text-muted-foreground whitespace-nowrap">{format(new Date(d), 'MMM d, yyyy h:mm a')}</span>
      },
    },
  ]

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
        <p className="font-medium">{error?.response?.status === 403 ? 'Access Denied' : 'Failed to load tasks'}</p>
        <p className="text-muted-foreground text-sm mt-1">{error?.response?.status === 403 ? 'You do not have access to this project.' : 'Something went wrong. Please try again.'}</p>
        {error?.response?.status === 403
          ? <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/projects')}>Back to Projects</Button>
          : <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
        }
      </div>
    )
  }

  return (
    <ProjectLayout>
    <div className="flex flex-col gap-4">
      {/* Search + Filters */}
      <div className="flex flex-col gap-3">
        <div className="relative max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <TaskFilters
          filters={filters}
          projectId={id}
          onChange={setFilters}
          statuses={statuses}
          users={users}
        />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={tasks}
        loading={isLoading}
        showSearch={false}
        emptyMessage="No tasks found. Try adjusting your filters."
        pageSize={20}
        enableRowSelection
        getRowId={(row) => String(row.id)}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />

      <BulkActionBar
        projectId={id}
        selectedIds={selectedIds}
        statuses={statuses}
        users={users}
        onClear={() => setRowSelection({})}
      />

    </div>
    </ProjectLayout>
  )
}

export default ProjectList
